import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Tab,
    Tabs,
    Card,
    CardContent
} from '@mui/material';
import {
    Api,
    VpnKey,
    Code as CodeIcon,
    MenuBook,
    Public
} from '@mui/icons-material';

// Syntax Highlighter style
const CodeBlock = ({ code, language = 'json' }) => (
    <Box sx={{
        bgcolor: '#1e1e1e',
        color: '#d4d4d4',
        p: 2,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        overflowX: 'auto',
        my: 1
    }}>
        <pre style={{ margin: 0 }}>
            <code>{code}</code>
        </pre>
    </Box>
);

/**
 * Every public endpoint accepts an optional `environment`. Omitting it serves
 * the workspace's default environment (Production), which is what integrations
 * written before environments existed already send — so their calls are
 * unchanged.
 */
const ENVIRONMENT_PARAM = {
    name: 'environment',
    type: 'Query',
    required: false,
    desc: "Environment id or name, e.g. ?environment=staging. Omit to read Production.",
};

const apiDocs = [
    {
        title: 'Get Entry',
        method: 'GET',
        path: '/entry/workspace/:workspaceId/content/:id/:depth/:locale?',
        description: 'Retrieve a single content entry by its ID. You can specify the depth of nested references and the locale.',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' },
            { name: 'id', type: 'Path', required: true, desc: 'ID of the entry' },
            { name: 'depth', type: 'Path', required: true, desc: 'Level of nested references to resolve (1-10)' },
            { name: 'locale', type: 'Path', required: false, desc: 'Locale code (default: en)' },
            { name: 'published', type: 'Query', required: false, desc: 'Filter by published status (true/false, default: true)' }
        ],
        exampleReq: null,
        exampleRes: `{
  "_id": "67becc...",
  "contentTypeId": "blog_post",
  "data": {
    "title": "My First Post",
    "body": "<p>Hello world...</p>"
  },
  "locale": "en",
  "published": true
}`
    },
    {
        title: 'Get Entries by Content Type',
        method: 'GET',
        path: '/entries/workspace/:workspaceId/content-type/:contentTypeId/:depth/:locale?',
        description: 'Retrieve a list of entries belonging to a specific content type.',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' },
            { name: 'contentTypeId', type: 'Path', required: true, desc: 'ID of the content type' },
            { name: 'depth', type: 'Path', required: true, desc: 'Level of nested references to resolve (1-10)' },
            { name: 'locale', type: 'Path', required: false, desc: 'Locale code (default: en)' }
        ],
        exampleReq: null,
        exampleRes: `[
  {
    "_id": "67becc...",
    "data": { "title": "Post 1" }
  },
  {
    "_id": "67becd...",
    "data": { "title": "Post 2" }
  }
]`
    },
    {
        title: 'Find Entry by Map',
        method: 'GET',
        path: '/entry/find/workspace/:workspaceId/content-type/:contentTypeId/content/:locale',
        description: 'Find an entry by querying specific fields (entity).',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' },
            { name: 'contentTypeId', type: 'Path', required: true, desc: 'ID of the content type' },
            { name: 'locale', type: 'Path', required: true, desc: 'Locale code' },
            { name: 'entity', type: 'Query', required: true, desc: 'JSON string of field-value pairs to match' },
            { name: 'published', type: 'Query', required: false, desc: 'Filter by published status (default: true)' }
        ],
        exampleReq: null,
        exampleRes: `{
  "_id": "67becc...",
  "data": { "slug": "my-post" }
}`
    },
    {
        title: 'Create Entry',
        method: 'POST',
        path: '/workspace/:workspaceId/entry/create',
        description: 'Create a new content entry from an external source.',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' }
        ],
        exampleReq: `{
            "identifier": "Rentals in Summer",
            "data": {
                "year": 2026,
                "createdDate": {
                    "date": "2026-02-25",
                    "time": "11:13",
                    "timeZone": "Asia/Colombo"
                },
                "showRelatedBlogs": "false",
                "bannerImage": [
                    {
                        "_id": "699fcf93130be992670ee5e9",
                        "title": "Summer Image",
                        "description": "Summer Scooter rentals image for blog page",
                        "url": "https://cog-cms-test.s3.us-west-2.amazonaws.com/cms/6954af784c3de0fbaae5af9b/images/screen.png"
                    }
                ],
                "hiddenHtml": "<div src="ai"></div>",
                "backgroundColor": "#703333"
            },
            "localeData": {
                "en": {
                    "title": "Scooter rentals you like in Summer",
                    "content": [
                        {
                            "type": "paragraph",
                            "children": [
                                {
                                    "text": "People like scooter rentals in Summer"
                                }
                            ]
                        }
                    ],
                    "highlights": [
                        "Summer",
                        "Blog"
                    ],
                    "schema": {
                        "type": "webpage"
                    }
                }
            },
            "references": {
                "author": [
                    112957
                ]
            },
            "workspaceId": "6954af784c3de0fbaae5af9b",
            "contentTypeId": "699fce81130be992670ee5e8",
            "version": 1,
            "published": true
}`,
        exampleRes: `{
    "status": "success",
    "message": "Entry created",
    "entryId": 112983
}`,
        notes: (
            <>
                <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                    Use the field ID in the request, not the field name.
                </Typography>

                <Typography variant="body1" gutterBottom>
                    Inside the <strong>data</strong> key, add field types such as:
                    number, date, boolean, media, numberArray, code, and color.
                </Typography>

                <Typography variant="body1" gutterBottom>
                    Inside the <strong>localeData</strong> key, include the locale. Within that,
                    add field types such as: text, richText, stringArray, and object.
                </Typography>

                <Typography variant="body1">
                    Inside the <strong>references</strong> key, add the entry ID of the referenced entry.
                </Typography>
            </>
        )
    },
    {
        title: 'Update Entry',
        method: 'POST',
        path: '/workspace/:workspaceId/entry/update/:id/:locale',
        description: 'Update an existing content entry.',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' },
            { name: 'id', type: 'Path', required: true, desc: 'ID of the entry to update' },
            { name: 'locale', type: 'Path', required: true, desc: 'Locale code' }
        ],
        exampleReq: 
        `{
            "title": "Winter",
            "year": 2026
}`,
        exampleRes: `{
            "status": "success",
            "message": "Entry updated successfully"
}`
    },
    {
        title: 'Upload Media',
        method: 'POST',
        path: '/api/asset/workspace/:workspaceId/upload',
        description: 'Upload a file (image, PDF, etc.) to the CMS from an external source.',
        params: [
            { name: 'workspaceId', type: 'Path', required: true, desc: 'ID of the workspace' },
            { name: 'file', type: 'File', required: true, desc: 'The file to upload (Multipart/form-data)' },
            { name: 'title', type: 'Body', required: false, desc: 'Title of the asset' },
            { name: 'description', type: 'Body', required: false, desc: 'Description of the asset' }
        ],
        exampleReq: `{
            "file": "[File Object]",
            "title": "Summer Image",
            "description": "Summer Scooter rentals image for blog page",
            "workspaceId": "6954af784c3de0fbaae5af9b"
        
}`,
        exampleRes: `{
    "status": "success",
    "message": "Asset uploaded",
    "entryId": "699fd81c130be992670ee5eb",
    "asset": {
        "title": "Summer Image",
        "description": "Summer Scooter rentals image for blog page",
        "workspaceId": "6954af784c3de0fbaae5af9b",
        "uploadedUser": "6953cb637be12c0f669b5f64",
        "fileName": "screen.png",
        "url": "https://cog-cms-test.s3.us-west-2.amazonaws.com/cms/6954af784c3de0fbaae5af9b/images/screen.png",
        "type": "image",
        "mimetype": "image/png",
        "createdAt": "2026-02-26T05:20:28.902Z",
        "_id": "699fd81c130be992670ee5eb"
    }
}`
    }
];

const Documentation = () => {
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box mb={6} textAlign="center">
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                    API Documentation
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                    Comprehensive guide to consuming the CMS Public Content API.
                </Typography>
            </Box>

            {/* Getting Started & Auth */}
            <Grid container spacing={4} mb={8}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 4, height: '100%' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <MenuBook color="primary" sx={{ mr: 1, fontSize: 32 }} />
                            <Typography variant="h5" fontWeight="600">Getting Started</Typography>
                        </Box>
                        <Typography paragraph>
                            The CMS provides a set of RESTful APIs to retrieve and manage your content programmatically.
                            These APIs are designed to be consumed by your frontend applications (websites, mobile apps)
                            or other backend services.
                        </Typography>
                        <Typography paragraph>
                            <strong>Base URL:</strong> <code>https://api.cms.cloudofgoods.com</code>
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 4, height: '100%' }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <VpnKey color="secondary" sx={{ mr: 1, fontSize: 32 }} />
                            <Typography variant="h5" fontWeight="600">Authorization</Typography>
                        </Box>
                        <Typography paragraph>
                            All public API requests must be authenticated using an API Key.
                            You can generate an API Key from your Workspace Settings.
                        </Typography>
                        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Header Requirement:</Typography>
                            <code style={{ fontSize: '1rem', color: '#d32f2f' }}>x-api-key: YOUR_API_KEY</code>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Box mb={6}>
                <Paper elevation={2} sx={{ p: 4 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Public color="primary" sx={{ mr: 1, fontSize: 32 }} />
                        <Typography variant="h5" fontWeight="600">Environments</Typography>
                    </Box>
                    <Typography paragraph>
                        A workspace can hold several environments — isolated copies of its content
                        models and entries. Every workspace has one default environment,
                        <strong> Production</strong>.
                    </Typography>
                    <Typography paragraph>
                        Add <code>?environment=&lt;name&gt;</code> to any request below to read or
                        write another one. <strong>Requests that do not name an environment are
                        served Production</strong>, so existing integrations need no change.
                    </Typography>
                    <CodeBlock
                        language="bash"
                        code={`# Production (unchanged)
GET /api/entries/workspace/:workspaceId/content-type/:contentTypeId/2/en

# Staging
GET /api/entries/workspace/:workspaceId/content-type/:contentTypeId/2/en?environment=staging`}
                    />
                    <Typography variant="body2" color="text.secondary">
                        Content models are per-environment and have their own ids, so use the
                        content-model ids belonging to the environment you are querying.
                    </Typography>
                </Paper>
            </Box>

            <Divider sx={{ mb: 8 }} />

            {/* API Reference */}
            <Box mb={4}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Api sx={{ mr: 1, color: 'primary.main' }} /> Public API Reference
                </Typography>
                <Typography color="text.secondary" mb={4}>
                    Explore the available endpoints. Click on tabs to switch between different resources.
                </Typography>

                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                            {apiDocs.map((doc, index) => (
                                <Tab key={index} label={doc.title} iconPosition="start" icon={doc.method === 'POST' ? <CodeIcon fontSize="small" /> : <Public fontSize="small" />} />
                            ))}
                        </Tabs>
                    </Box>

                    {apiDocs.map((doc, index) => (
                        <div role="tabpanel" hidden={activeTab !== index} key={index}>
                            {activeTab === index && (
                                <Card variant="outlined">
                                    <CardContent sx={{ p: 4 }}>
                                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                            <Chip
                                                label={doc.method}
                                                color={doc.method === 'GET' ? 'success' : 'primary'}
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                            <Typography variant="h6" fontFamily="monospace">
                                                {doc.path}
                                            </Typography>
                                        </Box>
                                        <Typography paragraph color="text.secondary">
                                            {doc.description}
                                        </Typography>

                                        <Divider sx={{ my: 3 }} />

                                        <Typography variant="h6" gutterBottom>Parameters</Typography>
                                        <List dense disablePadding>
                                            {[...doc.params, ENVIRONMENT_PARAM].map((param, i) => (
                                                <ListItem key={i} sx={{ px: 0 }}>
                                                    <ListItemText
                                                        primary={
                                                            <span>
                                                                <strong>{param.name}</strong>
                                                                <Chip label={param.type} size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7em' }} />
                                                                {param.required && <Chip label="Required" size="small" color="error" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: '0.7em' }} />}
                                                            </span>
                                                        }
                                                        secondary={param.desc}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>

                                        <Divider sx={{ my: 3 }} />

                                        <Grid container spacing={4}>
                                            {doc.exampleReq && (
                                                <Grid item xs={12} md={12}>
                                                    <Typography variant="h6" gutterBottom>Example Request Body</Typography>
                                                    <CodeBlock code={doc.exampleReq} />
                                                    {doc.notes}
                                                </Grid>
                                            )}
                                            <Grid item xs={12} md={12}>
                                                <Typography variant="h6" gutterBottom>Example Response</Typography>
                                                <CodeBlock code={doc.exampleRes} />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    ))}
                </Box>
            </Box>

            <Box mt={8} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                    For any issues or further assistance, please contact the support team.
                </Typography>
            </Box>
        </Container>
    );
};

export default Documentation;
